import Clarity from '@microsoft/clarity';

import { env } from '@/util/env';
import { Sentry } from '@/util/sentry';

const PROJECT_ID = env.EXPO_PUBLIC_CLARITY_PROJECT_ID;

const isEnabled = Boolean(PROJECT_ID);

const reportedFailures = new Set<string>();

/**
 * Clarity is non-critical telemetry, so a vendor failure must never reach the
 * user, but is reported rather than hidden so a broken SDK contract does not
 * silently end session capture.
 *
 * Reports once per call site for the life of the JS context. These wrappers run
 * on every navigation, so a contract that breaks would otherwise report on
 * every route change for every user, which is how third-party SDK chatter has
 * buried real errors here before. One report carries the same information.
 *
 * Only synchronous throws land here. Clarity documents its calls as returning
 * a promise the SDK discards, so a late rejection is out of reach, though that
 * path cannot block a render either.
 */
function reportClarityFailure(error: unknown, context: string) {
  if (reportedFailures.has(context)) {
    return;
  }

  reportedFailures.add(context);

  Sentry.captureException(error, {
    level: 'warning',
    tags: { context: `clarity:${context}` },
  });
}

/**
 * A page load that starts on a SMART EHR route carries the launch handle or the login token
 * in its address, and Clarity records the address with the session. Such a document is not
 * recorded at all, which also leaves the EHR session that continues in it unrecorded.
 */
function isSmartAuthDocument(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/smart');
}

/**
 * Initializes the Microsoft Clarity web SDK.
 *
 * Call once at app startup. No-op when `EXPO_PUBLIC_CLARITY_PROJECT_ID` is unset,
 * so environments without the variable do not record sessions.
 *
 * Needs no guard: the SDK catches its own injection errors and loads the tag
 * async, so a blocked or failing CDN cannot reach the caller.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-setup
 */
export function initClarity() {
  if (!isEnabled || isSmartAuthDocument()) {
    return;
  }

  Clarity.init(PROJECT_ID!);
}

/**
 * Associates the current Clarity session with a custom user ID.
 *
 * Per Clarity's guidance, should be called on each page when a user is
 * authenticated to stitch sessions across devices and visits. The ID is hashed
 * client-side before transmission.
 *
 * Unlike init, the SDK forwards this to the global Clarity queue unguarded, and
 * the app calls it from a bare effect at the root of the tree, so an unguarded
 * throw would take down every authenticated page.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api
 */
export function setClarityUserId(userId: string) {
  if (!isEnabled) {
    return;
  }

  try {
    Clarity.identify(userId);
  } catch (error) {
    reportClarityFailure(error, 'setClarityUserId');
  }
}

/**
 * Attaches a custom tag to the current Clarity session for dashboard filtering.
 *
 * Forwarded to the global Clarity queue unguarded, same as the user ID call.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/custom-tags
 */
export function setClarityTag(key: string, value: string) {
  if (!isEnabled) {
    return;
  }

  try {
    Clarity.setTag(key, value);
  } catch (error) {
    reportClarityFailure(error, 'setClarityTag');
  }
}

/** No-op on web, where Clarity names each page by its URL. */
export function setClarityScreenName(_screenName: string) {}
